import React from 'react';
import { Helmet } from 'react-helmet-async';

type BreadcrumbItem = {
  name: string;
  path: string;
};

export type LandingSeoProps = {
  title: string;
  description: string;
  path: string;
  breadcrumbs?: BreadcrumbItem[];
  lang?: string;
};

const SITE_NAME = 'SWARMIND AI';
const SITE_URL = 'https://swarmind.ai';

function toAbsoluteUrl(path: string) {
  if (!path.startsWith('/')) return `${SITE_URL}/${path}`;
  return `${SITE_URL}${path}`;
}

export const LandingSeo: React.FC<LandingSeoProps> = ({ title, description, path, breadcrumbs, lang }) => {
  const canonicalUrl = toAbsoluteUrl(path);

  const websiteJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL,
    inLanguage: lang,
    hasPart: [
      { '@type': 'SiteNavigationElement', name: 'Home', url: toAbsoluteUrl('/') },
      { '@type': 'SiteNavigationElement', name: 'Unified API', url: toAbsoluteUrl('/unified-api') },
      { '@type': 'SiteNavigationElement', name: 'GPU rental', url: toAbsoluteUrl('/gpu-rental') },
      { '@type': 'SiteNavigationElement', name: 'For developers', url: toAbsoluteUrl('/for-developers') },
    ],
  };

  const breadcrumbJsonLd =
    breadcrumbs && breadcrumbs.length >= 2
      ? {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: breadcrumbs.map((item, idx) => ({
            '@type': 'ListItem',
            position: idx + 1,
            name: item.name,
            item: toAbsoluteUrl(item.path),
          })),
        }
      : null;

  return (
    <Helmet>
      {lang ? <html lang={lang} /> : null}

      <title>{title}</title>
      <link rel="canonical" href={canonicalUrl} />

      <meta name="description" content={description} />

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />

      <meta property="twitter:card" content="summary" />
      <meta property="twitter:title" content={title} />
      <meta property="twitter:description" content={description} />

      <script type="application/ld+json">{JSON.stringify(websiteJsonLd)}</script>
      {breadcrumbJsonLd ? <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script> : null}
    </Helmet>
  );
};

